import * as THREE from "three";
import { MATS } from "../materials.js";
import * as H from "./spec.js";

/**
 * The upper storey of the hero house, from the client's second drawing.
 *
 * This is interior.js one floor up, and it invents no dimension either.  The
 * boards are laid on top of the ground floor's ceiling at H.UP.floor (3.50),
 * the lid is H.UP.ceiling (6.22, a hand's width below the eave so the roof
 * furniture the shell beds into the wall top clears it), and the walls are
 * the same brick seen from the other side -- the shell has always enclosed
 * this volume, it just had nothing in it.
 *
 * The organising line is the storey below's own: PLAN.divX, the long wall
 * that runs the full depth of the house on both floors, with the stair
 * climbing beside it.  Everything else hangs off that.
 *
 *      z-11.20 +--------------------------+---------------+
 *              |  MASTER                  |  EN-SUITE     |
 *              |  left -7.60, back -0.30  |  back 3.40    |
 *              |  [ROBE]                  |  right -8.20  |
 *      z -7.00 +-------------+------------+---------------+  masterZ
 *              |  BATHROOM   |  CORRIDOR  |  BEDROOM      |
 *              |  left -4.60 |            |  the bay      |
 *              |  [ROBE]     | [[ WELL ]] |  right -2.30  |
 *      z -2.60 +-------------+---+........|               |  frontZ
 *              |  BEDROOM        |  DESK  |               |
 *              |  the arch       |  pairR |               |
 *              |  + pair L       | [DESK] |               |
 *      z  1.20 +-----------------+--------+---------------+
 *             x -5.45       -1.00 -0.05      1.75       5.45
 *                           corrX0 deskX     divX
 *
 * The dotted stretch is what makes the plan work.  East of deskX there is no
 * wall in the frontZ line at all: the corridor and the desk nook are one
 * space, and the nook is the landing in all but name.  You come up the
 * flight, step off heading -Z on to the arrival strip, and everything is
 * reached from there -- the master straight ahead, the bathroom and the nook
 * to the west and north, the east bedroom off the nook, the en-suite through
 * the master.  The two lines drawn as one at z -7.00 are in fact two, 0.07
 * apart: masterZ west of the long wall and eastZ east of it.
 *
 * The well is a hole INSIDE the corridor rather than a room boundary, which
 * is why the corridor runs all the way to the long wall.  Stop it at the
 * well's edge and the arrival strip you land on belongs to no room, so
 * nothing lays a floor or a skirting on it.
 *
 * Every sill up here is low.  Measured off the boards: the arch 0.25, the bay
 * 0.40, the front pair 0.475, the four side windows 0.55 and 0.60 -- against
 * 0.78 to 1.05 downstairs.  Nothing was tuned to get that; the elevation was
 * drawn for a taller room than the storey actually has, so its windows land
 * near the floor.  It is also the reason this floor is worth climbing to: a
 * 0.25 m sill is a perch a cat steps onto, not one it has to plan for, and
 * reveal() gives every one of them a solid board to land on.
 *
 * Nearly bare, like the storey below: plaster, boards, skirting, linings and
 * reveals.  The drawing adds three built-ins, and they are the only furniture
 * either storey has -- two wardrobes and a desk, all three of them perches.
 *
 * Collision: the floor is four boxes around the stairwell rather than one --
 * the hole has to be genuinely absent or the stair climbs 2.95 m into a lid.
 * Each solid wall run is one box, each window sill is one box, the desk and
 * the robe shelves are one each, and nothing else is solid at all.
 */

const U = H.UP;
const F = U.floor;                       // 3.50
const C = U.ceiling;                     // 6.22
const T = U.wallT;                       // 0.12
const X0 = U.minX, X1 = U.maxX;          // -5.45 .. 5.45
const Z0 = U.minZ, Z1 = U.maxZ;          // -11.20 .. 1.20
const S = H.STAIRWELL;                   // the hole, shared with interior.js

const PL = MATS.plaster;
const PC = MATS.ceiling;                 // ceilings and downward soffits
const FB = MATS.floorBoard;
const FS = MATS.floorShade;
const TR = MATS[H.LOOK.trim];
const GL = MATS.glassLit;

const LINE = 0.03;                       // plaster skim over the brick
const TOP = C + 0.03;                    // walls die 3 cm INTO the ceiling
// slab rather than stopping flush with it: coplanar faces shimmer, and this
// junction runs right round every room.
const DOOR_H = 2.05;                     // internal doorway, above the floor
const DW = 1.05;                         // internal doorway width
// Indoors, `tint` is 0 rather than merely small.  Walls are emitted as one box
// per run between the holes in them, so per-piece variation does not read as
// variation at all -- it reads as bands drawn across the plaster.  What little
// AO is left darkens each piece's own base, which is what keeps the skirting
// and the reveals from floating.
const FLAT = { solid: false, ao: 0.04, tint: 0 };

// The plan, and every line of it is a spec export.  The long wall is not
// even this storey's: PLAN.divX is the one the ground floor already stands
// on, so the two plans cannot drift apart and the stair climbs beside it on
// both.
const DIV_X = H.PLAN.divX;               //  1.75  the long wall, full depth
const MASTER_Z = H.UPPER.masterZ;        // -7.00  master / corridor + bath
const FRONT_Z = H.UPPER.frontZ;          // -2.60  corridor + bath / front
const CORR_X0 = H.UPPER.corrX0;          // -1.00  bathroom / corridor
const DESK_X = H.UPPER.deskX;            // -0.05  front bedroom / desk nook
const EAST_Z = H.UPPER.eastZ;            // -6.93  en-suite / east bedroom

/**
 * Where the doorways go.
 *
 * Two of the five are the spec's own -- UPPER.ensuiteDoor and UPPER.eastDoor,
 * both through the long wall -- because both had to dodge something on the
 * far side of it and the plan is where that was settled.  The other three are
 * placed here, each by what it has to miss:
 *
 *   master    x -0.25  the one stretch of the masterZ wall with the full
 *                      depth of the corridor in front of it instead of the
 *                      well 0.92 m away.  That stretch runs -0.94 (the
 *                      bathroom partition's east face) to 0.45 (the well's
 *                      west edge), which is 1.39 m, so a 1.05 m opening fits
 *                      in exactly one place and leaves 0.17 m either side.
 *   bathroom  z -4.80  the middle of the corrX0 wall, which runs the whole
 *                      4.40 m between the two cross-walls with nothing in
 *                      it.  Centred because nothing argues for anywhere
 *                      else, and it is the shortest walk from both ends.
 *   bedroom   z -1.60  in the deskX wall at the nook's south end, so the
 *                      front bedroom opens off the nook as you enter it
 *                      rather than past the desk.  0.42 m of return below it
 *                      to the frontZ wall.
 *
 * No doorway can foul a window: every hole in the outer walls up here is a
 * window and all five openings are in partitions.  Whether a partition's own
 * END lands in a window is a separate question, and walls() keeps that note.
 */
const DOOR_MASTER = -0.25;
const DOOR_BATH = -4.80;
const DOOR_FRONT = -1.60;

/**
 * The internal walls: which plane each stands in, how far it runs, and the
 * doorways left in it.  partitions() builds from this and skirting() reads
 * the same runs, so a wall that moves takes its skirting with it.
 *
 * Two of them are worth reading twice.  The frontZ wall stops at deskX
 * instead of crossing the house, which is what leaves the corridor and the
 * desk nook one space and is the only way into the front half of the storey.
 * And masterZ and eastZ are 0.07 apart rather than in line -- one is the
 * master's back wall, the other the en-suite's -- so they come at the long
 * wall from opposite sides and both die inside it.
 */
const PARTITIONS = [
  { axis: "x", at: MASTER_Z, u0: X0, u1: DIV_X,
    doors: [[DOOR_MASTER, DW]] },
  { axis: "x", at: EAST_Z, u0: DIV_X, u1: X1, doors: [] },
  { axis: "x", at: FRONT_Z, u0: X0, u1: DESK_X, doors: [] },
  { axis: "z", at: CORR_X0, u0: MASTER_Z, u1: FRONT_Z,
    doors: [[DOOR_BATH, DW]] },
  { axis: "z", at: DESK_X, u0: FRONT_Z, u1: Z1,
    doors: [[DOOR_FRONT, DW]] },
  { axis: "z", at: DIV_X, u0: Z0, u1: Z1,
    doors: [[H.UPPER.ensuiteDoor.z, H.UPPER.ensuiteDoor.w],
            [H.UPPER.eastDoor.z, H.UPPER.eastDoor.w]] },
];

const ROOMS = [
  { name: "master",   x0: X0,      x1: DIV_X,   z0: Z0,       z1: MASTER_Z },
  { name: "ensuite",  x0: DIV_X,   x1: X1,      z0: Z0,       z1: EAST_Z },
  { name: "bathroom", x0: X0,      x1: CORR_X0, z0: MASTER_Z, z1: FRONT_Z },
  { name: "corridor", x0: CORR_X0, x1: DIV_X,   z0: MASTER_Z, z1: FRONT_Z },
  { name: "bedFront", x0: X0,      x1: DESK_X,  z0: FRONT_Z,  z1: Z1 },
  { name: "desk",     x0: DESK_X,  x1: DIV_X,   z0: FRONT_Z,  z1: Z1 },
  { name: "bedEast",  x0: DIV_X,   x1: X1,      z0: EAST_Z,   z1: Z1 },
];

// ---------------------------------------------------------------------------
// Wall-local placement.  `u` runs along the wall, `t` through it; "x" walls
// face along z, "z" walls face along x.  Every helper below is written once
// in these terms and works on all four sides of every room.
function wallBox(b, axis, uc, y, tc, uw, h, td, mat, opts = FLAT) {
  if (axis === "x") b.block(uc, y, tc, uw, h, td, mat, opts);
  else b.block(tc, y, uc, td, h, uw, mat, opts);
}

function wallCollider(b, axis, uc, y, tc, uw, h, td) {
  if (axis === "x") b.collider(uc, y, tc, uw, h, td);
  else b.collider(tc, y, uc, td, h, uw);
}

/** `[a0,a1]` minus every `[g0,g1]`, as the runs that survive. */
function runs(a0, a1, gaps) {
  let out = [[a0, a1]];
  for (const [g0, g1] of gaps) {
    out = out.flatMap(([s0, s1]) => {
      if (g1 <= s0 || g0 >= s1) return [[s0, s1]];
      const keep = [];
      if (g0 > s0) keep.push([s0, g0]);
      if (g1 < s1) keep.push([g1, s1]);
      return keep;
    });
  }
  return out.filter(([s0, s1]) => s1 - s0 > 1e-3);
}

/**
 * A thin panel on a wall plane with rectangles cut out of it.
 *
 * The shell's brickwork trick, one layer in: split the plane at every hole
 * edge, merge the closed cells across each band, and emit one box per run.
 * The plaster then meets every reveal exactly, with nothing to trim by hand.
 */
function panel(b, { axis, at, into, u0, u1, y0, y1, holes = [], mat, t = LINE }) {
  const cut = holes.map((r) => ({
    u0: Math.max(r.u0, u0), u1: Math.min(r.u1, u1),
    y0: Math.max(r.y0, y0), y1: Math.min(r.y1, y1),
  })).filter((r) => r.u1 > r.u0 && r.y1 > r.y0);
  const edges = (vals, lo, hi) => {
    const out = [];
    for (const v of [...vals].sort((p, q) => p - q)) {
      if (v < lo - 1e-4 || v > hi + 1e-4) continue;
      if (!out.length || v - out[out.length - 1] > 1e-4) out.push(v);
    }
    return out;
  };
  const us = edges([u0, u1, ...cut.flatMap((r) => [r.u0, r.u1])], u0, u1);
  const ys = edges([y0, y1, ...cut.flatMap((r) => [r.y0, r.y1])], y0, y1);
  const open = (uc, yc) =>
    cut.some((r) => uc > r.u0 + 1e-4 && uc < r.u1 - 1e-4 &&
                    yc > r.y0 + 1e-4 && yc < r.y1 - 1e-4);
  const tc = at + into * t / 2;
  for (let j = 0; j < ys.length - 1; j++) {
    const ya = ys[j], yb = ys[j + 1], yc = (ya + yb) / 2;
    let run = null;
    for (let i = 0; i < us.length; i++) {
      const closed = i < us.length - 1 && !open((us[i] + us[i + 1]) / 2, yc);
      if (closed && run === null) run = us[i];
      if (!closed && run !== null) {
        // Two things keep the bands from drawing themselves on the wall.
        // ao 0, not merely small: a run spans only its own band, so the
        // base-darkening would restart at every window head and sill.  And
        // 5 mm of overlap into the band above, because bands that butt share
        // a horizontal plane and that seam shimmers along its whole length.
        wallBox(b, axis, (run + us[i]) / 2, ya, tc, us[i] - run,
                yb - ya + 0.005, t, mat, { solid: false, ao: 0, tint: 0 });
        run = null;
      }
    }
  }
}

/**
 * The sides of a hole in an exterior wall, seen from the room.
 *
 * `depth` is how far back the reveal runs before it meets whatever fills the
 * opening.  A window also gets an inner sill: 0.20 m of board, and the one
 * piece here that is solid, because a sill at this height is the perch the
 * whole room is worth climbing for.
 */
function reveal(b, { axis, at, into, u0, u1, y0, y1, depth, sill = false }) {
  const uc = (u0 + u1) / 2, uw = u1 - u0;
  const tc = at + into * depth / 2;
  // The head board laps the plaster instead of butting it, and this is the
  // one line where this reveal() is not interior.js's.  panel() lays the band
  // above a window as a 0.03 skim on the room face of the brick, and that
  // band has an underside: 3 cm of PLASTER pointing straight down across the
  // full width of every opening, with only the lawn-green half of the
  // hemisphere reaching it.  Butted up to it, the head board sits 3 cm behind
  // that strip and cannot hide it -- so the board runs 1 cm proud of the skim
  // and 1 cm up into it, which buries the face and leaves a shadow line at
  // the head instead.  lining() has always done exactly this for door heads
  // ("2 cm up into the header"); this is the same move one opening type on.
  wallBox(b, axis, uc, y1 - LINE, at + into * (depth - LINE - 0.01) / 2,
          uw, LINE + 0.01, depth + LINE + 0.01, PC);      // head
  for (const s of [-1, 1])
    wallBox(b, axis, uc + s * (uw - LINE) / 2, y0, tc, LINE, y1 - y0, depth, PL);
  if (!sill) return;
  // Daylight pane at the back of the reveal.  The glazing itself is opaque
  // dark glass -- right from the street, a black rectangle from a room -- so
  // the inside gets its own bright face just in front of it.
  wallBox(b, axis, uc, y0 + 0.05, at + into * (depth - 0.02), uw - 0.10,
          y1 - y0 - 0.10, 0.02, GL, { solid: false, ao: 0, tint: 0 });
  const sd = depth + 0.13;                       // projects into the room
  wallBox(b, axis, uc, y0 - 0.06, at + into * depth / 2 - into * 0.065,
          uw + 0.12, 0.06, sd, TR);
  wallCollider(b, axis, uc, y0 - 0.06, at + into * depth / 2 - into * 0.065,
               uw + 0.12, 0.06, sd);
}

/**
 * A cased opening: boards lining the reveal, standing 2 cm proud of the
 * plaster on both faces.
 *
 * They sit INSIDE the opening, overlapping its edge by 5 mm.  Set outside it
 * instead and the frame vanishes: the reveal you actually look at is then the
 * wall's own cut face, and all the lining does is put a 5 cm strip of a very
 * slightly different white on a wall of white.
 *
 * The head board is the ceiling material, not trim.  It is a downward face,
 * and the hemisphere fill paints those lawn-green.
 */
function lining(b, axis, at, uc, uw, thick, y0 = F, h = DOOR_H) {
  const a = uw / 2, d = thick + 0.04;
  for (const s of [-1, 1])
    wallBox(b, axis, uc + s * (a - 0.02), y0, at, 0.05, h + 0.02, d, TR);
  // 2 cm up into the header and 1.5 cm into each jamb, so no two faces meet.
  wallBox(b, axis, uc, y0 + h - 0.05, at, uw - 0.06, 0.07, d, PC,
          { solid: false, ao: 0, tint: 0 });
}

// ---------------------------------------------------------------------------
/**
 * Boards over the whole storey, with the stairwell genuinely missing.
 *
 * Four rectangles round the hole rather than a general cutter -- the same
 * four bands interior.js cuts its ceiling into, read off the same
 * H.STAIRWELL, so the floor and the ceiling under it cannot end up
 * disagreeing about where the hole is.
 *
 * The collider is the load-bearing half of that.  Its top is exactly UP.floor
 * and its underside exactly UP.soffit: 0.15 m of structure, which is thin for
 * a real floor and thick enough that a cat cannot find a gap between the two
 * storeys to squeeze through.  One box per band, and the well is simply not
 * one of the bands.
 */
function floor(b) {
  const bands = [
    [X0, S.x0, Z0, Z1],           // west of the well: most of the storey
    [S.x1, X1, Z0, Z1],           // east of it, beyond the long wall
    [S.x0, S.x1, Z0, S.z0],       // behind it: the arrival strip, and on back
    [S.x0, S.x1, S.z1, Z1],       // in front of it, on into the desk nook
  ];
  for (const [x0, x1, z0, z1] of bands) {
    // Subfloor, dark: it sits ON the ground floor's ceiling slab (3.35..3.44)
    // rather than through it, and the boards lap 2 cm down into it, so no two
    // horizontal faces in the whole build-up are coplanar.
    b.block((x0 + x1) / 2, F - 0.06, (z0 + z1) / 2, x1 - x0, 0.05, z1 - z0,
            MATS.timber, { solid: false, ao: 0 });
    b.collider((x0 + x1) / 2, U.soffit, (z0 + z1) / 2, x1 - x0,
               F - U.soffit, z1 - z0);
  }

  // The boards themselves, wall to wall, on one pitch across the storey.  A
  // row that meets the well is cut twice: across it in x for the full-depth
  // runs either side, and along it in z for whatever slice of the row still
  // clears the hole's own edge.  Without the second cut a row that straddles
  // the edge would simply be dropped and the boards would step back from the
  // opening by up to half a board.
  const pitch = 0.19;
  const n = Math.round((Z1 - Z0) / pitch);
  const gap = (Z1 - Z0) / n;
  for (let i = 0; i < n; i++) {
    const zc = Z0 + (i + 0.5) * gap;
    const za = zc - (gap - 0.008) / 2, zb = zc + (gap - 0.008) / 2;
    const mat = i % 3 === 2 ? FS : FB;
    const lay = (a0, a1, c0, c1) =>
      b.block((a0 + a1) / 2, F - 0.03, (c0 + c1) / 2, a1 - a0, 0.03, c1 - c0,
              mat, { solid: false, ao: 0, tile: 0.9 });
    if (zb <= S.z0 || za >= S.z1) { lay(X0, X1, za, zb); continue; }
    for (const [a0, a1] of runs(X0, X1, [[S.x0, S.x1]])) lay(a0, a1, za, zb);
    for (const [c0, c1] of runs(za, zb, [[S.z0, S.z1]]))
      lay(S.x0, S.x1, c0, c1);
  }

  // The rim of the well.  Looking up through it from the hall you see the cut
  // ends of the boards, the subfloor and the ceiling slab -- 0.15 m of edge
  // with nothing but the lawn-green half of the hemisphere reaching it.  A
  // 15 mm band of MATS.ceiling standing just inside the opening covers all
  // three at once, and stays thin because the flight is exactly as wide as
  // the hole it comes up through.
  const rim = (x, z, w, d) =>
    b.block(x, U.soffit, z, w, F - U.soffit, d, PC,
            { solid: false, ao: 0, tint: 0 });
  rim((S.x0 + S.x1) / 2, S.z0 + 0.009, S.x1 - S.x0, 0.015);
  rim((S.x0 + S.x1) / 2, S.z1 - 0.009, S.x1 - S.x0, 0.015);
  rim(S.x0 + 0.009, (S.z0 + S.z1) / 2, 0.015, S.z1 - S.z0);
  rim(S.x1 - 0.009, (S.z0 + S.z1) / 2, 0.015, S.z1 - S.z0);
}

/**
 * Flat ceiling, 0.08 m below the eave.
 *
 * The roof void above it is nobody's room -- the hip springs from the eave
 * and the cat is on the outside of it -- so this is a lid and not a floor.
 * The 0.08 is H.UP.ceiling's own, and it buys clearance: the shell beds its
 * roof furniture 0.02 into the wall top, and at the eave this slab left the
 * dormer's brick cheeks hanging below it like beams.
 */
function ceiling(b) {
  // Oversized by 4 cm so its edges finish inside the brick, not on it.
  b.block(0, C, (Z0 + Z1) / 2, X1 - X0 + 0.08, 0.09, Z1 - Z0 + 0.08, PC,
          { solid: false, ao: 0, tint: 0 });
}

/**
 * Which of the shell's holes are this storey's business.
 *
 * The complement of interior.js's `ours` (y1 <= 3.35).  Every window in the
 * house comes back `through` now that there is a room behind all of them, so
 * `through` alone no longer says which floor an opening belongs to.  panel()
 * clips its holes to the wall it is drawing and quietly drops the ones off
 * it, but reveal() does not: unfiltered, this file would hang the living
 * room's sill and its daylight pane down at 1.32 m, inside the storey below.
 * Between the two tests every hole in the brick gets lined exactly once.
 */
const ours = (w) => w.y0 >= F;

/**
 * The bay window, which is a room's worth of window rather than a window.
 *
 * The shell builds a box in front of this opening and openings.js glazes it
 * 0.78 m forward of the wall plane, so the flat windows' treatment is wrong
 * here twice over.  The reveal is deep -- the brick lined nearly right
 * through, stopping 2 cm short of the stone floor slab the shell lays inside
 * the box.
 *
 * And the daylight goes one plane further out.  reveal() puts its bright face
 * at the back of the reveal, which is right when the glazing is 0.10 m behind
 * the plaster and quite wrong here: a pane on the wall line would be a sheet
 * of frosted white sealing bed 1 off from the one thing the room exists for.
 * But leaving it out is not the answer either -- MATS.glass is opaque dark by
 * design, so the showpiece window read as a black rectangle in a house where
 * every other window reads as daylight, which is the exact defect reveal()
 * was written to prevent.  So the same trick is played against the bay's OWN
 * glazing: three lit faces, the wide front one and the two angled cheeks,
 * each a couple of centimetres inboard of the dark glass on that pane's own
 * plane.  Nothing changes from the street, because the glazing in front of
 * them is opaque.
 *
 * What replaces the sill is a seat.  0.42 m into the room, 0.08 m thick, and
 * standing 2 cm proud of the opening so its top is clear of the brick's own
 * sill face instead of coplanar with it -- 2 cm is also exactly the height
 * openings.js sets its bay sill board at, so the seat and the bay's own sill
 * read as one ledge running out to the glass.  It is the best perch in the
 * house and it is meant to be.
 */
function bayReveal(b, o) {
  const deep = H.WALL_T - 0.06;                  // 0.24
  reveal(b, { axis: "x", at: Z1, into: 1, ...o, depth: deep });
  bayDaylight(b, o);

  const uc = (o.u0 + o.u1) / 2, uw = o.u1 - o.u0 + 0.12;
  const zb = Z1 + deep, zf = Z1 - 0.42;
  const top = o.y0 + 0.02;
  b.block(uc, top - 0.08, (zb + zf) / 2, uw, 0.08, zb - zf, TR,
          { ao: 0.04, tint: 0 });                // solid: the perch itself
  // The seat's underside is a downward face wide enough to notice, and the
  // hemisphere fill paints those lawn-green.  A skin of MATS.ceiling under
  // the part that projects, lapped 5 mm up into the board so the two do not
  // share a plane.
  b.block(uc, top - 0.105, (Z1 + 0.01 + zf - 0.01) / 2, uw - 0.03, 0.03,
          Z1 - zf + 0.02, PC, { solid: false, ao: 0, tint: 0 });
}

/**
 * The bay's three lit faces, on the bay's own three panes.
 *
 * Every number here is read off H.BAY so these land on the same planes
 * openings.js glazed: `fh` is the front pane's half-width once the cheeks
 * have eaten `cheek` of plan width each side, and a cheek is the hypotenuse
 * of (cheek, project) in plan, yawed to match.  The one number of its own is
 * the 0.095 offset along each pane's normal, which sets the face 1.3 cm
 * inboard of the dark glass -- clear of it, and clear of the frame members
 * and muntins, which all sit further out still.  Being nearest the room it
 * hides them, exactly as the flat windows' pane hides theirs.
 *
 * 0.08 in off every edge of the glazed rectangle, which is inside both the
 * front pane's 0.07 frame and the cheeks' 0.06 one, so a single pair of
 * numbers serves all three and no face can peer past its own frame.
 */
function bayDaylight(b, o) {
  const B = H.BAY;
  const p = H.opening("bay").plane;             // FACE, the wall plane
  const front = p + B.project;                  // the wide pane's plane
  const fh = B.width / 2 - B.cheek;             // 0.95, front pane half-width
  const y0 = B.sill + 0.08, h = B.head - B.sill - 0.16;
  const lit = (px, pz, ry, w) => {
    const g = new THREE.BoxGeometry(w, h, 0.02);
    g.translate(0, y0 + h / 2, -0.095);
    if (ry) g.rotateY(ry);
    g.translate(px, 0, pz);
    b.add(g, GL, { collider: null, ao: 0, tint: 0 });
  };
  lit(B.centreX, front, 0, 2 * fh - 0.16);
  const len = Math.hypot(B.cheek, B.project);
  const ry = Math.atan2(B.project, B.cheek);
  for (const s of [-1, 1])
    lit(B.centreX + s * (fh + B.cheek / 2), (p + front) / 2, s * ry,
        len - 0.16);
}

/** Plaster over the brick, cut round every window on this floor. */
function walls(b) {
  const rect = (o) => ({ id: o.id, u0: o.x - o.w / 2, u1: o.x + o.w / 2,
                         y0: o.y - o.h / 2, y1: o.y + o.h / 2 });
  const back = H.WALL_T - 0.095;         // reveal depth to the window frames

  // --- front wall: the arch, the upper pair, and the bay ---
  // The arch is cut and lined as the rectangle the masonry actually leaves;
  // the curve is openings.js's tympanum panel, hung at the back of the recess
  // behind everything this file draws.
  //
  // One clash to know about, and it is the plan's rather than this file's:
  // the deskX partition lands 0.035 m inside the west light of the front
  // pair.  UPPER.deskX was already pulled out to -0.05 to get the wall out of
  // the MIDDLE of that window; 0.12 of wall centred there still takes the
  // last 3.5 cm of a 0.95 m opening, and pushing it further east would put
  // the nook's own window at risk instead.  The wall dies into the reveal,
  // which is cheaper than a jog in the plan.
  const upper = H.OPENINGS.map(rect).filter(ours);
  panel(b, { axis: "x", at: Z1, into: -1, u0: X0, u1: X1, y0: F, y1: TOP,
             holes: upper, mat: PL });
  for (const o of upper) {
    if (o.id === "bay") { bayReveal(b, o); continue; }
    reveal(b, { axis: "x", at: Z1, into: 1, ...o, depth: back, sill: true });
  }

  // --- back wall ---
  const bw = H.sideWindows("back").filter(ours);
  panel(b, { axis: "x", at: Z0, into: 1, u0: X0, u1: X1, y0: F, y1: TOP,
             holes: bw, mat: PL });
  for (const w of bw)
    reveal(b, { axis: "x", at: Z0, into: -1, ...w, depth: back, sill: true });

  // --- the two side walls ---
  // Nothing straddles a room boundary on either of these any more: the left
  // wall's upper window moved to -4.60 so it sits square in the bathroom, and
  // the right wall's two both fall inside a single room.  The plan was fitted
  // to the windows rather than the other way round.
  for (const [wall, at, into] of [["left", X0, 1], ["right", X1, -1]]) {
    const wins = H.sideWindows(wall).filter(ours);
    panel(b, { axis: "z", at, into, u0: Z0, u1: Z1, y0: F, y1: TOP,
               holes: wins, mat: PL });
    for (const w of wins)
      reveal(b, { axis: "z", at, into: -into, ...w, depth: back, sill: true });
  }
}

/**
 * The five internal walls.
 *
 * Each is one plastered stud with its doorways left open and lined; the solid
 * runs carry the colliders, the headers over the doorways do not (they start
 * at 5.55, and a cat that jumps 0.95 off the floor tops out at 4.45).
 */
function partitions(b) {
  for (const { axis, at, u0, u1, doors } of PARTITIONS) {
    const gaps = doors.map(([c, w]) => [c - w / 2, c + w / 2]);
    for (const [a0, a1] of runs(u0, u1, gaps)) {
      wallBox(b, axis, (a0 + a1) / 2, F, at, a1 - a0, TOP - F, T, PL);
      wallCollider(b, axis, (a0 + a1) / 2, F, at, a1 - a0, C - F, T);
    }
    for (const [c, w] of doors) {
      wallBox(b, axis, c, F + DOOR_H, at, w, TOP - F - DOOR_H, T, PL);
      lining(b, axis, at, c, w, T);
    }
  }
}

const near = (a, c) => Math.abs(a - c) < 0.02;
const isOuter = (at) => [Z0, Z1, X0, X1].some((v) => near(at, v));

/**
 * The stretches of a wall plane that actually have a wall in them.
 *
 * Skirting has to follow this rather than the outline of the room, because
 * the landing's z = -3.40 edge is open along its whole length: run a board
 * round the room and you get a skirting board standing in mid-air across the
 * way through to the corridor.
 *
 * The brick returns unbroken up here, where downstairs it had to be split
 * around three doors.  There is no external doorway on this floor -- every
 * hole in the outer walls is a window, and the lowest sill of the lot (the
 * arch, 0.25 m above the boards) clears a 0.12 m board with 0.13 to spare.
 */
function wallRunsOn(axis, at, a0, a1) {
  const door = (c, w) => [c - w / 2 - 0.09, c + w / 2 + 0.09];
  if (isOuter(at)) return [[a0, a1]];
  const out = [];
  for (const p of PARTITIONS) {
    if (p.axis !== axis || !near(p.at, at)) continue;
    out.push(...runs(Math.max(p.u0, a0), Math.min(p.u1, a1),
                     p.doors.map(([c, w]) => door(c, w))));
  }
  return out;
}

/**
 * Skirting along every wall a room actually has, stopped at each doorway --
 * and stopped again wherever there is no floor under it.
 *
 * The well is hard against the long wall now: STAIRWELL.x1 IS that
 * partition's west face.  So the corridor's east skirting would run the whole
 * 4.40 m of it with 3.08 m hanging over the hole.  wallRunsOn is not wrong --
 * the wall really does run the whole way -- what is missing is the floor, so
 * each run is cut by the well as well.  The test is made at the board's own
 * position rather than the wall's, because the board is the thing that has to
 * be standing on something.  Nothing else on this storey meets the well: on
 * its other three sides it is a hole in the middle of the corridor, and the
 * guarding round it is stairs.js's.
 */
function skirting(b) {
  const h = 0.12, t = 0.025;
  for (const r of ROOMS) {
    for (const [axis, at, into, a0, a1] of [
      ["x", r.z1, -1, r.x0, r.x1], ["x", r.z0, 1, r.x0, r.x1],
      ["z", r.x0, 1, r.z0, r.z1], ["z", r.x1, -1, r.z0, r.z1],
    ]) {
      const inset = isOuter(at) ? LINE : T / 2;   // plaster skim vs partition
      const tc = at + into * (inset + t / 2);
      const over = axis === "z" ? tc > S.x0 && tc < S.x1
                                : tc > S.z0 && tc < S.z1;
      const well = over ? [axis === "z" ? [S.z0, S.z1] : [S.x0, S.x1]] : [];
      for (const [w0, w1] of wallRunsOn(axis, at, a0, a1))
        for (const [s0, s1] of runs(w0, w1, well)) {
          if (s1 - s0 < 0.12) continue;
          wallBox(b, axis, (s0 + s1) / 2, F, tc, s1 - s0, h, t, TR);
        }
    }
  }
}

/**
 * The two built-in wardrobes the drawing puts against the west wall.
 *
 * Each is a plaster carcass with almost nothing to it: one side panel, a
 * bulkhead over the opening, and no back and no west side at all -- the back
 * is the cross-wall it is built against and the west side is the brick, which
 * is the entire point of building one into a corner.  The opening faces -Z,
 * into whichever room the footprint actually sits in.  UPPER.robes[0] is
 * z -7.60..-7.00, on the master's side of masterZ, so it is the master's;
 * UPPER.robes[1] is z -3.20..-2.60, which is on the BATHROOM's side of
 * frontZ, so it is the bathroom's.  (The spec's inline comment calls the
 * second one the front bedroom's; the rectangle is 0.60 m the other way.
 * Built where the rectangle is, since that is the export.)
 *
 * No doors, deliberately: a wardrobe a cat cannot get into is a wall with a
 * handle on it, which is the same argument CLOSET makes downstairs for
 * leaving one of its leaves slid open.  Head, shelf and rail are not invented
 * either -- they are CLOSET's own three heights, measured off that storey's
 * floor and re-hung off this one, so the built-ins in the house match.
 *
 * The master's robe overlaps the north half of that room's west window; see
 * the note in build().
 */
function robes(b) {
  const head = F + (H.CLOSET.head - H.INSIDE.floor);      // 5.15
  const shelfY = F + (H.CLOSET.shelf - H.INSIDE.floor);   // 4.80
  const railY = F + (H.CLOSET.rail - H.INSIDE.floor);     // 4.67
  for (const r of H.UPPER.robes) {
    const zc = (r.z0 + r.z1) / 2, zd = r.z1 - r.z0;
    const uc = (r.x0 + r.x1 - T) / 2, uw = r.x1 - T - r.x0;   // clear opening
    b.block(r.x1 - T / 2, F, zc, T, TOP - F, zd, PL, FLAT);   // side panel
    b.collider(r.x1 - T / 2, F, zc, T, C - F, zd);
    b.block(uc, head, r.z0 + T / 2, uw, TOP - head, T, PL, FLAT);  // bulkhead
    b.collider(uc, head, r.z0 + T / 2, uw, C - head, T);
    lining(b, "x", r.z0 + T / 2, uc, uw, T, F, head - F);
    // Shelf set back behind the bulkhead so it meets no lining board, and the
    // rail hung under it the way CLOSET hangs its own.
    const sz0 = r.z0 + T;
    b.block(uc, shelfY, (sz0 + r.z1) / 2, uw - 0.10, 0.03, r.z1 - sz0, TR,
            { ao: 0.04, tint: 0 });
    b.bar(uc, railY - 0.016, (sz0 + r.z1) / 2, 0.016, uw - 0.10, "x",
          MATS.metalPale, { solid: false, ao: 0, segments: 8 });
  }
}

/**
 * The built-in desk under the front-east window.
 *
 * The one piece of furniture either storey has, and it is here because the
 * drawing asks for it and because of what 0.78 m is: the sills in this room
 * are 0.475 above the boards and the floor is 0, so the desk is the middle
 * rung of the only flight of surfaces upstairs has.  A cat gets from the
 * floor to the desk to the window pair without being asked to jump once.
 *
 * Two end panels and a top.  The ends are solid, because a 0.04 m board you
 * walk through is a worse bug than one you walk round, but the middle is left
 * open so under the desk is somewhere to be as well as on it.  The underside
 * of the top is a downward face and takes the ceiling material, like every
 * other downward face in the house.
 */
function desk(b) {
  const d = H.UPPER.desk;
  const top = F + d.top, t = 0.04;               // 4.28
  const zc = (d.z0 + d.z1) / 2, zd = d.z1 - d.z0;
  const uc = (d.x0 + d.x1) / 2, uw = d.x1 - d.x0;
  for (const x of [d.x0 + t / 2, d.x1 - t / 2])
    b.block(x, F, zc, t, top - t - F, zd, TR, { ao: 0.04, tint: 0 });
  b.block(uc, top - t, zc, uw, t, zd, TR, { ao: 0.04, tint: 0 });
  // 5 mm up into the top, so the skin and the board share no plane.
  b.block(uc, top - t - 0.025, zc, uw - 0.06, 0.03, zd - 0.06, PC,
          { solid: false, ao: 0, tint: 0 });
}

// ---------------------------------------------------------------------------
export function build(b) {
  floor(b);
  walls(b);
  partitions(b);
  robes(b);
  desk(b);
  skirting(b);
  ceiling(b);

  for (const r of ROOMS)
    b.mark(`up.${r.name}`, (r.x0 + r.x1) / 2, F, (r.z0 + r.z1) / 2);

  // The corridor's own centre is 0.075 m off the lip of the well, which is no
  // place to put anything that might get spawned there.  Marked on the strip
  // west of it instead -- the part of the corridor that runs its full depth.
  b.mark("up.corridor", (CORR_X0 + S.x0) / 2, F, (MASTER_Z + FRONT_Z) / 2);

  // Where the flight lets go.  It climbs -Z, so S.z0 is what the spec calls
  // the head, and this is the arrival strip you step off on to with the
  // master's door ahead of you.
  b.mark("up.stairhead", (S.x0 + S.x1) / 2, F, S.z0 - 0.40);
}
